import React, { useState } from "react";
import "./reunite.scss";
import { Box, Container, Grid } from "@mui/material";
import SEO from "../../seo/SEO";

function ReunitePage() {
  const [feedback, setFeedback] = useState("");
  const [submittedChoice, setSubmittedChoice] = useState(null);

  const timelineData = [
    {
      id: 1,
      title: "The Separation",
      subtitle: "Crowds can overwhelm children and the elderly",
      description:
        "In the midst of thousands of devotees, a moment of distraction is all it takes. A child lets go of a hand or an elder falls behind, the crowd swallows them whole.",
      image: "/assets/reunite-page/story/step-1.webp",
    },
    {
      id: 2,
      title: "The Discovery",
      subtitle: "A stranger becomes a helper",
      description:
        "A kind pilgrim notices the child wearing the Reunite Band. A quick scan of the wristband QR code with any phone camera no app required.",
      image: "/assets/reunite-page/story/step-2.webp",
    },
    {
      id: 3,
      title: "The Reunion",
      subtitle: "Family restored, journey continues",
      description:
        "Emergency contacts appear instantly. Within minutes the family is reunited, and the sacred pilgrimage continues with peace of mind.",
      image: "/assets/reunite-page/story/step-3.webp",
    },
  ];

  const steps = [
    {
      icon: "/assets/reunite-page/icons/register.svg",
      title: "Register Details",
      desc: "Add emergency contacts, medical info, blood group and allergies via the app.",
    },
    {
      icon: "/assets/reunite-page/icons/band.svg",
      title: "Get Your Band",
      desc: "Order a Reunite Band for each vulnerable family member before your pilgrimage.",
    },
    {
      icon: "/assets/reunite-page/icons/journey.svg",
      title: "Wear During Journey",
      desc: "Secure the waterproof band on the wrist which is comfortable enough for all-day wear.",
    },
    {
      icon: "/assets/reunite-page/icons/found.svg",
      title: "Scan If Found",
      desc: "Anyone can scan the QR code with any phone camera and no app is required for the finder.",
    },
  ];

  const features = [
    {
      icon: "/assets/reunite-page/icons/qr.svg",
      title: "QR Code Security",
      description:
        "Anyone who finds a lost yatri can scan the band to instantly access emergency contact numbers.",
    },
    {
      icon: "/assets/reunite-page/icons/shield.svg",
      title: "For Vulnerable Yatris",
      description:
        "Perfect for small children and elderly family members who may get separated in crowded environments.",
    },
    {
      icon: "/assets/reunite-page/icons/medical.svg",
      title: "Vital Medical Info",
      description:
        "Stores crucial details like blood group and allergies for immediate medical assistance.",
    },
    {
      icon: "/assets/reunite-page/icons/mind.svg",
      title: "Peace of Mind",
      description:
        "Enjoy your spiritual journey knowing your loved ones are always within reach.",
    },
  ];

  return (
    <div className="reunite-page">
      <SEO pageKey="reuniteBand" />
      {/* ==================== 1. HERO BANNER ==================== */}
      <Box className="reunite-hero">
        <Container maxWidth="lg">
          <Grid container alignItems="center">
            <Grid size={{ xs: 12, md: 7, lg: 6 }}>
              <div className="hero-content">
                <span className="hero-badge">Coming Soon</span>
                <h1>
                  Never Lose Your <br />
                  <span className="gold-text">Loved Ones</span>
                </h1>
                <p className="hero-desc">
                  A smart wristband with a scannable QR code that is designed to protect your family during crowded spiritual journeys.
                </p>
              </div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ==================== 2. THE JOURNEY OF REUNION ==================== */}
      <section className="journey-reunion-section">
        <Container maxWidth="lg">
          <div className="section-header text-center">
            <h2>The Journey of Reunion</h2>
            <p className="section-subtitle">
              Every separation carries a story and every Reunite Band writes a new ending.
            </p>
          </div>

          <div className="journey-progress-bar">
            <div className="progress-track-line"></div>
            <div className="progress-steps-row">
              {timelineData.map((item) => (
                <div key={item.id} className="progress-step-item">
                  <span className="step-circle">{item.id}</span>
                </div>
              ))}
            </div>
          </div>

          <Grid container spacing={3.5} className="journey-cards-grid">
            {timelineData.map((item) => (
              <Grid key={item.id} size={{ xs: 12, md: 4 }}>
                <div className="journey-story-card">
                  <div className="card-thumb">
                    <img src={item.image} alt={item.title} />
                  </div>
                  <div className="card-body">
                    <h4 className="card-tag">{item.title}</h4>
                    <h5 className="card-sub">{item.subtitle}</h5>
                    <p className="card-text">{item.description}</p>
                  </div>
                </div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </section>

      {/* ==================== 3. HOW IT WORKS ==================== */}
      <section className="how-it-works-section">
        <Container maxWidth="lg">
          <div className="section-header text-center">
            <span className="section-label">SIMPLE STEPS</span>
            <h2>How It Works</h2>
          </div>

          <Grid container spacing={3} className="how-grid">
            {steps.map((item, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
                <div className="work-step-card">
                  <div className="step-icon-box">
                    <img src={item.icon} alt={item.title} />
                  </div>
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </section>

      {/* ==================== 4. WHY REUNITE BAND? ==================== */}
      <section className="why-reunite-section">
        <Container maxWidth="lg">
          <div className="section-header text-center">
            <span className="section-label">SACRED SHIELD</span>
            <h2>Why Reunite Band?</h2>
            <p className="section-subtitle">Four layers of protection for every pilgrim.</p>
          </div>

          <Grid container spacing={2.5} className="why-grid">
            {features.map((item, index) => (
              <Grid key={index} size={{ xs: 12, md: 6 }}>
                <div className="why-feature-card">
                  <div className="feature-icon-box">
                    <img src={item.icon} alt={item.title} />
                  </div>
                  <div className="feature-info">
                    <h4>{item.title}</h4>
                    <p>{item.description}</p>
                  </div>
                </div>
              </Grid>
            ))}
          </Grid>

          <div className="why-quote-box">
            <p>The greatest offering at any temple is the safety of those who journey with you.</p>
          </div>
        </Container>
      </section>

      {/* ==================== 5. SHARE YOUR THOUGHTS ==================== */}
      <section className="share-thoughts-section">
        <Container maxWidth="md">
          <div className="thoughts-card">
            <div className="card-header-row">
              <div className="header-icon-box">
                <img src="/assets/reunite-page/icons/feedback.svg" alt="Share Your Thoughts" />
              </div>
              <div className="header-text">
                <span className="header-label">YOUR VOICE MATTERS</span>
                <h3>Share Your Thoughts</h3>
              </div>
            </div>

            <p className="question-text">
              Would you order this sacred shield for your family's next pilgrimage?
            </p>

            <textarea
              className="thoughts-textarea"
              placeholder="Any thoughts or suggestions? (Optional)"
              rows={3}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />

            <div className="thoughts-actions-row">
              <button
                type="button"
                className={`btn-essential ${submittedChoice === "essential" ? "active" : ""}`}
                onClick={() => setSubmittedChoice("essential")}
              >
                Yes, this is essential
              </button>
              <button
                type="button"
                className={`btn-not-now ${submittedChoice === "not_now" ? "active" : ""}`}
                onClick={() => setSubmittedChoice("not_now")}
              >
                Not right now
              </button>
            </div>

            <p className="thanks-text">Thank you for sharing.</p>
          </div>
        </Container>
      </section>
    </div>
  );
}

export default ReunitePage;